import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import d3_arc from '../utils/custom-d3-arc/index.js'
import { interpolate as d3_interpolate, interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import { select as d3_select } from 'd3-selection'
import SizeHelper from '../chart_helpers/SizeHelper'
import { wrapSvgText, ellipseSvgText } from '../utils/textUtils'
import { formatNumber } from '../utils/formatUtils'
import { pieChartLabels } from '../utils/labelUtils'
import { scaleLinear as d3_scaleLinear, scaleBand as d3_scaleBand } from 'd3-scale'
import * as COLORS from '../utils/colorUtils'
import { isDarkMode } from '../utils/configUtils.js'

export default class DonutProgressChartChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    mode: {
      type: String,
      default: 'pie',
      exposed: false,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    labelValue: {
      type: [String],
      default: 'none',
      options: ['none', 'abs', 'percent'],
      exposed: true,
    },
    shape: {
      type: String,
      default: 'full',
      options: ['full', 'half'],
      exposed: true,
    },
    /**
     * {-EN-} If set to `true`, sector values will be show as a percentage of the total.
     * {-ES-} Si se establece en `true`, los valores del sector se mostrarán como un porcentaje del total.
     */
    showPercentage: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} Format for the percentages.
     * {-ES-} Formato para los porcentajes.
     */
    percentageFormat: {
      type: [String],
      default: '[N]0,0.0',
      exposed: true,
    },
    /**
     * {-EN-} If a value is set, it will be displayed in the center of the donut. If set to `true`, the total sum of the sector values will be displayed.
     * {-ES-} Si se establece un valor, se mostrará en el centro del donut. Si se establece en `true`, se mostrará la suma total de los valores del sector.
     */
    centerValue: {
      type: [Boolean, Number, String, Object],
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} If a value is set to true, the selected item value will be displayed in the center of the donut
     * {-ES-} Si un valor se establece en verdadero, el valor del elemento seleccionado se mostrará en el centro del donut.
     */
    selectedItemCenterValue: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} If a value is set, it will be displayed in the above the central value of the donut.
     * {-ES-} Si se establece un valor, se mostrará arriba el valor central del donut.
     */
    centerTopText: {
      type: [String],
      // default: '<div><b>BOLD</b>&<i>ITALIC</i></div>',
      default: '',
      exposed: true,
    },
    /**
     * {-EN-} If a value is set, it will be displayed in the under the central value of the donut.
     * {-ES-} Si se establece un valor, se mostrará debajo del valor central del donut.
     */
    centerBottomText: {
      type: [String],
      // default: `<a href="https://www.bancosantander.es" target="_blank">link 1</a><span style="color: #f0f; text-decoration: underline; cursor: pointer" onclick="(function(){
      //   alert('Test Alert!');
      // })();">js function</span>`,
      default: '',
      exposed: true,
    },
    /**
     * {-EN-} If set to `true`, all bars will round its corners
     * {-ES-} Si se establece en `true`, todas las barras redondearán sus esquinas.
     */
    roundedBars: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} If set to `true`, transition animation will be in reversed direction
     * {-ES-} Si se establece en `true`, la animación de transición estará en dirección invertida
     */
    reversedTransition: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /**
     * {-EN-} Set a custom bar placeholder color
     * {-ES-} Establecer un color de marcador de posición de barra personalizado
     */
    placeholderColor: {
      type: String,
      default: 'sky',
      exposed: true,
    },
    /**
     * {-EN-} Thickness of the donut
     * {-ES-} Grosor del donut
     */
    donutThickness: {
      type: String,
      default: 'medium',
      options: ['thick', 'medium', 'thin'],
      exposed: true,
    },
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
    TooltipBehavior: {
      getHtml: function (d) {
        //Chapuzilla porque no esta en ningun axis, y no nos vale "formatValue(value,axis)"
        let formatter = (value, format) => {
          let match = this.matchFormatString(format || '')
          let numberFormat = match && match[2]
          let thouDel = this.config.thousandsDelimiter
          let decDel = this.config.decimalDelimiter
          return formatNumber(value, numberFormat, thouDel, decDel)
        }
        return `
          <div class="tip-value important">${this.formatTooltipValue(d.value)}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              { id: d.id }
            )}; margin-right: 3px"></span>
            ${d.id}
          </div>
          ${
            this.config.showPercentage
              ? '<div class="secondary">' +
                formatter(d.percent, this.config.percentageFormat) +
                '%</div>'
              : ''
          }
          `
      },
      getItemOffset: function ($el, d) {
        // let parentRect = this.root.node().getBoundingClientRect()
        let parentRect = $el.closest('.chart').getBoundingClientRect()

        let center = this._arc.centroid(d)
        // console.log(parentRect,center)
        return {
          x: parentRect.x + parentRect.width / 2 + center[0],
          y: parentRect.y + parentRect.height / 2 + center[1],
        }
      },
    },
  }

  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper]
  formatter(value, format) {
    let match = this.matchFormatString(format || '')
    let numberFormat = match && match[2]
    let thouDel = this.config.thousandsDelimiter
    let decDel = this.config.decimalDelimiter
    return formatNumber(value, numberFormat, thouDel, decDel)
  }
  beforeHelpers() {
    // this.content = this.root.selectAll('.content').data([null])
    // this.content = this.content.merge(this.content.enter().append('g').attr('class','content'))
  }
  getRadiuses() {
    let radius
    if (this.config.shape === 'half')
      radius = Math.min((0.75 * this.size.width) / 2, this.size.height / 1.35)
    else radius = Math.min((0.75 * this.size.width) / 2, this.size.height / 2)
    if (this.config.showLabels || this.config.showLabelIcons) radius = radius - 30
    let innerRadius
    if (this.config.mode === 'donut') {
      if (this.config.donutThickness === 'thick') innerRadius = 0.5 * radius
      else if (this.config.donutThickness === 'thin') innerRadius = 0.91 * radius
      else innerRadius = 0.7 * radius
    } else {
      innerRadius = 0.01 * radius
    }
    return { radius, innerRadius }
  }

  drawCenterValue(value) {
    const self = this

    let { radius, innerRadius } = this.getRadiuses()

    let fontSize = 28
    let centerValue = d3_select(this.$root)
      .selectAll('.center-value')
      .data(
        (this.config.mode === 'donut' &&
          this.config.centerValue &&
          this.config.centerValue !== 'none') ||
          value
          ? [null]
          : []
      )
    let centerValueEnter = centerValue
      .enter()
      .append('text')
      .attr('class', 'center-value')
      .style('opacity', 0)

    centerValue.exit().transition().style('opacity', 0).remove()
    centerValue = centerValue.merge(centerValueEnter)
    centerValue
      .attr(
        'transform',
        `translate(${this.size.width / 2},${
          this.size.height / 2 + (this.config.shape === 'half' ? radius * 0.2 : 0)
        })`
      )
      .attr('fill', 'currentColor')
      .attr('font-size', fontSize)
      .attr('font-family', 'santander-microtext-embedded')
      .attr('text-anchor', 'middle')
      // .attr('dominant-baseline','central')
      // .attr('alignment-baseline','central')
      .attr('y', '0.36em')

    // this.formatValue(this.data.reduce((sum,d)=>sum+d.value,0))

    // centerValueEnter.text(this.formatValue(centerValueValue)

    const totalValue =
      value || value === 0
        ? value
        : this.data.reduce((accumulator, current) => {
            return (accumulator += current.value)
          }, 0)
    let centerValueValue =
      value || value === 0
        ? value
        : this.config.centerValue === true
        ? totalValue
        : this.config.centerValue || null
    let isPair =
      centerValueValue &&
      (centerValueValue.hasOwnProperty('left') || centerValueValue.hasOwnProperty('right'))
    centerValueEnter.text('')
    let targetValue
    if (!isPair) {
      if (centerValueValue === 'avg') {
        const avgValue = totalValue / this.data.length
        centerValue
          .transition()
          .duration(this._transitionDuration)
          .textTween(function () {
            const i = d3_interpolate(this._current || 0, Number(avgValue))
            return function (t) {
              this._current = i(t)
              return self.valueWithUnit(this._current)
            }
          })
          .style('opacity', 1)
        targetValue = self.valueWithUnit(avgValue)
      } else if (!isNaN(Number(centerValueValue))) {
        centerValue
          .transition()
          .duration(this._transitionDuration)
          .textTween(function () {
            const i = d3_interpolate(this._current || 0, Number(centerValueValue))
            return function (t) {
              this._current = i(t)
              return self.valueWithUnit(this._current)
            }
          })
          .style('opacity', 1)
        targetValue = self.valueWithUnit(centerValueValue)
      } else {
        // if(centerValueValue.indexOf('<')>=0){
        //   let fo = centerValue.selectAll('foreignObject').data([null],(d)=>d)
        //   let foEnter = fo.enter().append('foreignObject')
        //     .attr('class','foreignObject')
        //   foEnter.append('xhtml:div')
        //     .attr('class','html-container')
        //   fo = fo.merge(foEnter)
        //   fo.select('div').html(centerValueValue)
        //   targetValue = null
        // } else {
        centerValue.text(centerValueValue).transition().style('opacity', 1)
        targetValue = centerValueValue
        // }
      }
    } else {
      centerValue
        .html(
          `<tspan font-size="1em" font-weight="bold" x="-${
            centerValueValue.left.length * 0.3
          }em" dx="-6" y="0.36em">${
            centerValueValue.left
          }</tspan><tspan font-size="0.7em" x="0" y="0.36em"> / </tspan><tspan font-size="0.7em" x="${
            centerValueValue.right.length * 0.3
          }em" dx="6" y="0.36em">${centerValueValue.right}</tspan>`
        )
        .transition()
        .style('opacity', 1)
      targetValue = null
    }

    if (centerValue.node() && targetValue) {
      let tmp = centerValue.text()
      centerValue.text(targetValue)
      let l = centerValue.node().getComputedTextLength()
      while (l > 1.8 * innerRadius && fontSize > 10) {
        fontSize = fontSize - 1
        centerValue.attr('font-size', fontSize)
        l = centerValue.node().getComputedTextLength()
      }
      centerValue.text(tmp)
    }
  }

  draw() {
    let self = this
    const angleSclaeRange =
      this.config.shape === 'half' ? [-Math.PI / 2, Math.PI / 2] : [0, 2 * Math.PI]
    const angleScale = d3_scaleLinear().domain([0, 100]).range(angleSclaeRange)

    const { radius: maxRadius, innerRadius: minRadius } = this.getRadiuses()
    const radiusScale = d3_scaleBand()
      .paddingInner(0.5)
      .domain(this.data.map(d => d.id))
      .range([minRadius, maxRadius])

    this.data.forEach(d => {
      d.startAngle = angleScale(0)
      d.endAngle = angleScale(d.percent)
    })

    // Store the displayed angles in _current.
    // Then, interpolate from _current to the new angles.
    // During the transition, _current is updated in-place by d3_interpolate.
    function arcTween(a) {
      var i = d3_interpolate(this._current, { ...a })

      return function (t) {
        this._current = i(t)
        let arc = d3_arc()
          .innerRadius(radiusScale(i(t).id) - radiusScale.bandwidth())
          .outerRadius(radiusScale(i(t).id))
          .cornerRadius(20)
          .fullRounded(self.config.roundedBars)
        return arc(i(t))
      }
    }

    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    function key(d) {
      return d && d.data && d.data.id
    }
    let { radius, innerRadius } = this.getRadiuses()

    let arc = d3_arc()
      .innerRadius(d => radiusScale(d.id) - radiusScale.bandwidth())
      .outerRadius(d => radiusScale(d.id))
      .startAngle(angleScale(0))
      .endAngle(d => angleScale(d.percent))
      .cornerRadius(20)
      .fullRounded(self.config.roundedBars)

    this._arc = arc

    if (this.config.shape === 'half')
      this.content.attr(
        'transform',
        `translate(${this.size.width / 2},${this.size.height / 2 + radius * 0.4})`
      )
    else this.content.attr('transform', `translate(${this.size.width / 2},${this.size.height / 2})`)

    this.itemsEnter.append('path').attr('class', 'background-path')
    this.itemsEnter.append('path').attr('class', 'value-path')

    const getPlaceholderPathColor = d => {
      switch (this.config.placeholderColor.toLowerCase()) {
        case 'tint':
          return this.color({ id: d.id })
        case 'sky':
          return isDarkMode(this.config, this._parent.accessibilityOptions) ? '#444444' : '#f6fafc'
        case 'white':
          return isDarkMode(this.config, this._parent.accessibilityOptions)
            ? COLORS.SKIN_BACKGROUND_DARK
            : COLORS.SKIN_BACKGROUND
        default:
          return this.config.placeholderColor
      }
    }

    this.itemsEnter
      .select('.background-path')
      .attr('fill', getPlaceholderPathColor)
      .style('opacity', this.config.placeholderColor.toLowerCase() === 'tint' ? 0.2 : 1)
      .attr('d', function (d) {
        return arc({ ...d, percent: 100 })
      })

    this.items
      .select('.background-path')
      .attr('fill', getPlaceholderPathColor)
      .style('opacity', this.config.placeholderColor.toLowerCase() === 'tint' ? 0.2 : 1)
      .attr('d', function (d) {
        return arc({ ...d, percent: 100 })
      })

    this.itemsExitTrans.select('.background-path').style('opacity', 0)

    this.itemsEnter
      .select('.value-path')
      .attr('fill', d => this.color(d))
      .each(function (d, i) {
        this._current = {
          startAngle: angleScale(0),
          endAngle: self.config.reversedTransition ? angleScale(100) : angleScale(0),
        }
      })

    this.itemsEnterTrans
      .delay(0.1)
      .select('.value-path')
      .attrTween('fill', fillTween)
      .attrTween('d', arcTween)

    this.itemsTrans.select('.value-path').attrTween('fill', fillTween).attrTween('d', arcTween)

    this.itemsExitTrans.style('opacity', 0).select('.value-path').attrTween('d', arcTween)

    // this.itemsExit.datum(function(d, i) { return findNeighborArc(i, data1, data0, key) || d; })
    // this.itemsExitTrans.select('path').attrTween("d", arcTween)

    // CENTRAL VALUE
    //BASURAS HARDCODEADAS...
    let centerHeight = 20
    let centerMargin = 35
    let TOP_BOTTOM_OFFSET = 6
    this.drawCenterValue()

    let fontSize = 28

    // OTHER CENTER TEXTS
    let centerSizes = { width: innerRadius * 1.6, height: innerRadius }

    let svgTexts = [] // [{pos: 'top', text: this.config.centerTopText},{pos: 'bottom', text: this.config.centerBottomText}]
    let divTexts = [] //
    if (this.config.centerTopText && this.config.centerTopText.indexOf('<') >= 0)
      divTexts.push({ pos: 'top', text: this.config.centerTopText })
    else svgTexts.push({ pos: 'top', text: this.config.centerTopText })

    if (this.config.centerBottomText && this.config.centerBottomText.indexOf('<') >= 0)
      divTexts.push({ pos: 'bottom', text: this.config.centerBottomText })
    else svgTexts.push({ pos: 'bottom', text: this.config.centerBottomText })

    let centerTextsDivs = d3_select(this.$root)
      .selectAll('.center-text-div')
      .data(this.config.mode === 'donut' ? divTexts : [])
    let centerTextsSvgs = d3_select(this.$root)
      .selectAll('.center-text-svg')
      .data(this.config.mode === 'donut' ? svgTexts : [])

    // IF DIV TEXTS (NOT GOOD for exporting, but good for advanced devs)
    let centerTextsDivsEnter = centerTextsDivs
      .enter()
      .append('g')
      .attr('class', d => 'center-text-div ' + d.pos)
      .style('opacity', 0)

    centerTextsDivsEnter
      .append('foreignObject')
      .attr('class', 'foreignObject')
      .append('xhtml:div')
      .attr('class', 'html-container')
    centerTextsDivs.exit().transition().style('opacity', 0).remove()
    centerTextsDivs = centerTextsDivs.merge(centerTextsDivsEnter)

    centerTextsDivs.attr(
      'transform',
      d =>
        `translate(${this.size.width / 2 - centerSizes.width / 2},${
          this.size.height / 2 + (d.pos === 'top' ? -centerSizes.height : 0)
        })`
    )

    centerTextsDivs
      .select('.foreignObject')
      .attr('width', centerSizes.width)
      .attr('height', centerSizes.height)

    centerTextsDivs
      .select('.foreignObject')
      .select('.html-container')
      // .style('transform',(d,i)=>`translate(-50%,${i===0?'-100%':'0'})`)
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('height', '100%')
      .style('justify-content', d => (d.pos === 'top' ? 'flex-end' : 'flex-start'))
      .style('align-items', 'center')
      .style('text-align', 'center')
      .html(d => d.text)

    centerTextsDivs
      .select('.foreignObject')
      .select('.html-container')
      .transition()
      .style('padding-bottom', d => (this.config.centerValue && d.pos === 'top' ? '20px' : '0px'))
      .style('padding-top', d => (this.config.centerValue && d.pos === 'bottom' ? '20px' : '0px'))

    centerTextsDivs.transition().style('opacity', 1)

    // IF SVG TEXTS (GOOD for exporting)
    let centerTextsSvgsEnter = centerTextsSvgs
      .enter()
      .append('g')
      .attr('class', d => 'center-text-svg ' + d.pos)
      .style('opacity', 0)

    centerTextsSvgsEnter
      .append('g')
      .attr('class', 'foreignObject')
      .append('text')
      .attr('class', 'html-container')

    centerTextsSvgs.exit().transition().style('opacity', 0).remove()
    centerTextsSvgs = centerTextsSvgs.merge(centerTextsSvgsEnter)

    fontSize = 16
    centerTextsSvgs.attr(
      'transform',
      d =>
        `translate(${this.size.width / 2},${
          this.size.height / 2 +
          (this.config.shape === 'half' ? radius * 0.2 : 0) +
          (d.pos === 'bottom' ? centerMargin : centerHeight - centerMargin)
        })`
    )
    centerTextsSvgs
      .select('.foreignObject')
      .select('.html-container')
      .attr('fill', 'currentColor')
      .attr('font-size', fontSize)
      .attr('font-family', 'santander-microtext-embedded')
      .attr('text-anchor', 'middle')
      // .attr('dy','0.36em')
      // .attr('dominant-baseline',(d)=>d.pos==='top'?'central':'central')
      .text(d => d.text)
      .call(wrapSvgText, radius)
      .attr('transform', function (d) {
        let height = d.pos === 'top' ? this.getBBox().height : 0
        return `translate(0,${TOP_BOTTOM_OFFSET - height})`
      })
    centerTextsSvgs.transition().style('opacity', 1)

    // LABELS && Icons
    let getIcon = d => {
      return (
        !!this.config.showLabelIcons &&
        this.config.ItemsHelper.colorDict &&
        this.config.ItemsHelper.colorDict[d.id]?.icon?.icon &&
        this.config.ItemsHelper.colorDict[d.id]?.icon
      )
    }
    //Check if has icon or text in order to append label
    const showLabel = d => {
      if (this.config.showLabels || this.config.showLabelIcons) {
        return this.config.showLabelIcons ? !!getIcon(d) : true
      }
      return false
    }

    // this.itemsEnter.selectAll('.label').data([null]).enter().append('g').attr('class','label')
    let labels = this.items
      .merge(this.itemsEnter)
      .selectAll('.label')
      .data(d =>
        this.config.showLabels || (this.config.showLabelIcons && showLabel(d)) ? [d] : []
      )
    let labelsEnter = labels.enter().append('g').attr('class', 'label')
    labels.exit().remove()
    this.itemsExitTrans.select('.label').attr('opacity', 0)
    let r0 = radius * 0.05,
      dr = radius * 0.2,
      dh = radius * 0.1

    labelsEnter
      .append('path')
      .attr('stroke', 'currentColor')
      .attr('fill', 'none')
      .style('opacity', 0)

    //Icons
    const ICON_HEIGHT = 23
    const ICON_WIDTH = 23
    const ICON_OFFSET = 5

    /*
    labelsEnter
      .append('text')
      .attr('fill', 'currentColor')
      .attr('alignment-baseline', 'middle')
      .attr('font-size', '14')
      .attr('font-family', 'santander-microtext-embedded')
    */

    //const labelsMerge = labels.merge(labelsEnter)

    let outerCentroid = d => {
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      return [Math.cos(angle) * radius, Math.sin(angle) * radius]
    }
    //NO esta del todo bien... algunas veces hace "elbows rectos" muy feos...
    let labelPath = d => {
      // let center = this._arc.centroid(d)
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      let start = [center[0] + Math.cos(angle) * r0, center[1] + Math.sin(angle) * r0]
      // let joint = [center[0] + Math.cos(angle) * dr, center[1] + Math.sin(angle) * dr]
      let joint = [(d.y * Math.cos(angle)) / Math.sin(angle), d.y]
      //let end = [joint[0] + sign * dh, joint[1]]
      let end = [d.x - 3 * sign, d.y]
      //un poco cutre hacerlo con sort...
      let jointRange = [start[0], end[0]]
      jointRange.sort((a, b) => a - b)
      joint[0] = Math.min(Math.max(joint[0], jointRange[0]), jointRange[1])

      return `M ${start} L ${joint} L ${[end]}`
    }
    let labelTextAnchor = d => {
      // let center = this._arc.centroid(d)
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      return sign < 0 ? 'end' : 'start'
    }
    let labelTransform = function (d, offset) {
      // let center = this._arc.centroid(d)
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      let translate = []
      //Check if datum contains d. It means that forces placed it
      if (d.x) {
        translate = [d.x, d.y]
      } else {
        let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
        let joint = [(d.y * Math.cos(angle)) / Math.sin(angle), d.y]
        translate = [joint[0] + sign * dh * 1.2, joint[1]]
      }
      //Check offset (in order to place icons && texts together) and apply to translate
      if (!!offset) {
        translate[0] += offset[0] * sign
        translate[1] += offset[1]
      }
      return `translate(${translate})`
    }
    function pathTween(a) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelPath(i(t))
      }
    }
    function transTween(a, offset) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelTransform(i(t), offset)
      }
    }

    //const labelsMerge = labels.merge(labelsEnter)
    const customScale = d => {
      let center = outerCentroid(d)
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      let sign = center[0] > 0 ? 1 : -1
      // let start = [center[0]+Math.cos(angle)*r0,center[1]+Math.sin(angle)*r0]
      let joint = [center[0] + Math.cos(angle) * dr, center[1] + Math.sin(angle) * dr]
      let end = [joint[0] + sign * dh * 1.2, joint[1]]
      return end
    }

    labelsEnter.style('opacity', 0)
    labelsEnter
      .select('text')
      .attr('text-anchor', labelTextAnchor)
      .attr('transform', labelTransform)

    labels = labels.merge(labelsEnter)

    let labelsText = labels.selectAll('text').data(
      d => (this.config.showLabels ? [d] : []),
      d => d.id
    )
    labelsText.exit().remove()
    const labelsTextEnter = labelsText
      .enter()
      .append('text')
      .attr('fill', 'currentColor')
      .html(d => d.id)
      .attr('alignment-baseline', 'middle')
      .attr('font-size', '14')
      .attr('font-family', 'santander-microtext-embedded')

    labelsText = labelsText.merge(labelsTextEnter)

    const getLabelValue = d => {
      switch (this.config.labelValue) {
        case 'abs':
          return this.valueWithUnit(d.data.value)
        case 'percent':
          return this.formatter(d.data.percent, this.config.percentageFormat) + '%'
        default:
          return null
      }
    }
    labels.select('text').html(d => {
      let labelValue = getLabelValue(d)
      return (
        `<tspan alignment-baseline="middle">${d.id}${labelValue ? ': ' : ''}</tspan>` +
        (labelValue
          ? `<tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${labelValue}</tspan>`
          : '')
      )
    })
    labels.select('text').each(function (d) {
      let center = outerCentroid(d)
      // let r = self.getRadiuses().radius
      let remaining = self.size.width / 2 - Math.abs(center[0])
      let maxWidth = remaining - 60
      d3_select(this)
        .call(wrapSvgText, maxWidth)
        .selectAll('tspan')
        .each(function () {
          let str = getLabelValue(d)
          d3_select(this).attr('alignment-baseline', 'middle')
          if (str && this.innerHTML.indexOf(str) >= 0) {
            this.innerHTML = this.innerHTML.replace(str, ' ')
            d3_select(this.parentNode)
              .append('tspan')
              .text(str)
              .attr('font-weight', 'bold')
              .attr('alignment-baseline', 'middle')
            // this.innerHTML = this.innerHTML.replace(str,`<tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${str}</tspan>`)
          }
        })
    })

    //Icons
    let translate = [0, -ICON_HEIGHT / 2 - 2]

    let iconGroup = labels
      .selectAll('.icon')
      .data(d => (getIcon(d)?.icon ? [{ id: d.id, ...getIcon(d) }] : []))
    iconGroup.exit().remove()
    let iconGroupEnter = iconGroup.enter().append('g').attr('class', 'icon')
    //iconGroupEnter.style('opacity', 0).transition().duration(500).style('opacity', 1)
    iconGroup = iconGroup.merge(iconGroupEnter)
    iconGroup.attr('transform', `translate(${translate[0]}, ${translate[1]})`)
    iconGroup.html(d => (d ? d.icon.svg : null))
    let iconGroupSvg = iconGroup.select('svg')
    //Hay q hardcodear nuevos ids en los defs y en los use para hacer unicas las modificaciones
    iconGroupSvg
      .select('defs')
      .select('path')
      .attr('id', function (d) {
        return this.getAttribute('id') ? this.getAttribute('id') + `donut-chart-icon-${d.id}` : null
      })
    iconGroupSvg.select('use').attr('xlink:href', function (d) {
      return this.getAttribute('xlink:href')
        ? this.getAttribute('xlink:href') + `donut-chart-icon-${d.id}`
        : null
    })
    iconGroupSvg.selectAll('path, polygon').attr('fill', function () {
      let d = d3_select(this.parentNode).datum()
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })
    iconGroupSvg.attr('height', `${ICON_HEIGHT}px`).attr('width', `${ICON_WIDTH}px`)
    iconGroupSvg.select('use').attr('fill', function (d) {
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })

    let labelsTrans = labels.transition().duration(1000)
    //labelsTrans.select('text').attrTween('transform', transTween)
    pieChartLabels(labels, customScale, this.getRadiuses().radius)

    labelsTrans.style('opacity', 1)
    labelsTrans
      .select('path')
      .attrTween('d', pathTween)
      .transition()
      .duration(500)
      .style('opacity', 1)
    labelsTrans
      .select('text')
      .attr('text-anchor', labelTextAnchor)
      // .attr('transform', labelTransform)
      .attrTween('transform', function (d) {
        return transTween.call(this, d, getIcon(d)?.icon ? [ICON_WIDTH + 4, 0] : null)
      })
    labelsTrans
      .select('.icon')
      // .attr('transform', labelTransform)
      .attrTween('transform', function (d) {
        let center = outerCentroid(d)
        let sign = center[0] > 0 ? 1 : -1
        return transTween.call(this, d, [sign === -1 ? ICON_WIDTH : 0, (ICON_HEIGHT / 2) * -1 - 2])
      })

    // we must reset selection (NO ME GUSTA, lo quito)
    // this.selection = this.data.filter(d => this.selection.find(dd => dd.id === d.id) !== undefined)
    // this._updateSelection()
  }
  setSelectedFunc(sel, d) {
    if (this.config.selectedItemCenterValue) {
      this.drawCenterValue(d.value)
    }
  }
  unsetSelectedFunc(sel) {
    if ((this.config.selectedItemCenterValue && !this.selection) || this.selection.length === 0) {
      this.drawCenterValue()
    }
  }
}
